import React, { useState } from "react";
import { Avatar } from "@mui/material";
import { useDepartment } from "../../stores/departmentStore";
import { api } from "../../utils/axios-instance";
import toast from "react-hot-toast";

export default function OverviewTab({ room, roomId, darkMode, theme }) {
  const { departments } = useDepartment();
  const [memberSearch, setMemberSearch] = useState("");
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [clientEmailInput, setClientEmailInput] = useState("");
  const [submittingClientEmail, setSubmittingClientEmail] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(room);

  // Memoize department directory metadata maps
  const deptMap = React.useMemo(() => {
    const map = {};
    (departments || []).forEach((d) => {
      map[d.id] = {
        name: d.name ? d.name.replace("_", " ") : d.id,
        bg: d.metadata?.bg || "#6366f1",
      };
    });
    return map;
  }, [departments]);

  const syncDirectoryAndRoom = async () => {
    try {
      const [usersRes, roomRes] = await Promise.all([
        api.get(`/users/list_users`),
        api.get(`/vendors/rooms/`),
      ]);
      setOrgUsers(usersRes.data || []);
      const target = roomRes.data?.deliveryRooms?.find((r) => r.id === roomId);
      if (target) setCurrentRoom(target);
    } catch (err) {
      toast.error("Failed to refresh room directory parameters");
    }
  };

  React.useEffect(() => {
    syncDirectoryAndRoom();
  }, [roomId]);

  const handleAddMember = async (userObj) => {
    try {
      await api.post("/vendors/rooms/add-member/", {
        roomId: currentRoom.id,
        memberId: userObj.id,
        name: `${userObj.first_name} ${userObj.last_name}`,
        avatar: userObj.profilePictureUrl,
        department: userObj.dept,
      });
      toast.success("Team collaborator added successfully");
      setIsMemberModalOpen(false);
      setMemberSearch("");
      syncDirectoryAndRoom();
    } catch (err) {
      toast.error("Failed to add collaborator");
    }
  };

  const handleAddClientEmail = async (e) => {
    e.preventDefault();
    const emailClean = clientEmailInput.trim();
    if (!emailClean) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailClean)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (currentRoom.clientEmails?.includes(emailClean)) {
      toast.error("This email is already whitelisted for this room");
      return;
    }

    setSubmittingClientEmail(true);
    try {
      await api.post("/vendors/rooms/roster/", {
        roomId: currentRoom.id,
        type: "client",
        value: emailClean,
      });
      toast.success("Client email whitelisted successfully");
      setClientEmailInput("");
      syncDirectoryAndRoom();
    } catch (err) {
      toast.error("Failed to whitelist client email");
    } finally {
      setSubmittingClientEmail(false);
    }
  };

  const filteredAvailableEmployees = orgUsers.filter((u) => {
    const isEmployee =
      u.role_priv !== "org_admin" && u.role_priv !== "super_admin";
    const notInRoomYet = !currentRoom?.permittedUsers?.some(
      (member) => member.id === u.id
    );
    const matchesSearch =
      u.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
      `${u.first_name} ${u.last_name}`
        .toLowerCase()
        .includes(memberSearch.toLowerCase());
    return isEmployee && notInRoomYet && matchesSearch;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* 1. TEAM MEMBERS */}
      <section
        style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: theme.textMain }}>Team members</h3>
            <p style={{ fontSize: "12px", color: theme.textSub, margin: 0 }}>
              Internal collaborators who can work on this delivery room.
            </p>
          </div>
          <button
            onClick={() => setIsMemberModalOpen(true)}
            style={{
              background: "#5E5ADB",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            + Add member
          </button>
        </div>

        {!currentRoom.permittedUsers || currentRoom.permittedUsers.length === 0 ? (
          <p
            style={{
              fontSize: "13px",
              color: theme.textSub,
              fontStyle: "italic",
              margin: 0,
            }}
          >
            No team members added yet.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "12px",
              maxWidth: "900px",
            }}
          >
            {currentRoom.permittedUsers.map((member) => {
              const resolvedDept = deptMap[member.department] || {
                name: member.department || "No Department",
                bg: "#6366f1",
              };
              return (
                <div
                  key={member.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: darkMode
                      ? theme.cardBg
                      : `${resolvedDept.bg}15`,
                    border: `1px solid ${darkMode ? theme.border : resolvedDept.bg + "44"}`,
                    borderRadius: "10px",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      src={member.avatar}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        border: `1px solid ${resolvedDept.bg}`,
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme.textMain,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {member.name}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: darkMode ? theme.textSub : resolvedDept.bg,
                          fontWeight: "500",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={resolvedDept.name}
                      >
                        {resolvedDept.name}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await api.post("/vendors/rooms/remove-member/", {
                          roomId: currentRoom.id,
                          memberObj: member,
                        });
                        toast.success("Member access revoked");
                        syncDirectoryAndRoom();
                      } catch (err) {
                        toast.error("Failed to remove member");
                      }
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#c5221f",
                      fontSize: "12px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Revoke
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 2. CLIENT ACCESS */}
      <section
        style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h3 style={{ margin: 0, color: theme.textMain }}>Client access</h3>
          <p style={{ fontSize: "12px", color: theme.textSub, margin: 0 }}>
            Whitelisted target emails authorized to view and review deliverable
            versions.
          </p>
        </div>

        <form
          onSubmit={handleAddClientEmail}
          style={{
            display: "flex",
            gap: "12px",
            maxWidth: "500px",
            marginBottom: "20px",
          }}
        >
          <input
            type="email"
            required
            placeholder="client@company.com"
            value={clientEmailInput}
            onChange={(e) => setClientEmailInput(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              border: `1px solid ${theme.border}`,
              borderRadius: "6px",
              background: theme.bg,
              color: theme.textMain,
              fontSize: "14px",
            }}
          />
          <button
            type="submit"
            disabled={submittingClientEmail}
            style={{
              background: "#5E5ADB",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            {submittingClientEmail ? "Adding..." : "Add Email"}
          </button>
        </form>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {!currentRoom.clientEmails || currentRoom.clientEmails.length === 0 ? (
            <p
              style={{
                fontSize: "13px",
                color: theme.textSub,
                fontStyle: "italic",
                margin: 0,
              }}
            >
              No client contacts whitelisted for this room.
            </p>
          ) : (
            currentRoom.clientEmails.map((email, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 16px",
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "6px",
                  maxWidth: "500px",
                }}
              >
                <span style={{ fontSize: "14px", color: theme.textMain }}>
                  {email}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await api.post("/vendors/rooms/remove-client/", {
                        roomId: currentRoom.id,
                        email,
                      });
                      toast.success("Client access revoked");
                      syncDirectoryAndRoom();
                    } catch (err) {
                      toast.error("Failed to revoke client");
                    }
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#c5221f",
                    fontSize: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      // Hiding for now
      {/* 3. FREELANCER ACCESS */}
      {/* <section
        style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div>
            <h3 style={{ margin: 0, color: theme.textMain }}>
              Freelancer access
            </h3>
            <p style={{ fontSize: "12px", color: theme.textSub, margin: 0 }}>
              Invite a freelancer to upload receivables into this room.
            </p>
          </div>
          <button
            style={{
              background: "#5E5ADB",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "13px",
            }}
          >
            + Generate freelancer link
          </button>
        </div>
      </section> */}

      {/* 4. ROOM DETAILS */}
      <section
        style={{
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0", color: theme.textMain }}>
          Room Information
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "150px 1fr",
            gap: "12px",
            fontSize: "14px",
          }}
        >
          <span style={{ color: theme.textSub }}>Client Contact Name</span>{" "}
          <span style={{ color: theme.textMain }}>{currentRoom.clientName}</span>
          <span style={{ color: theme.textSub }}>
            Active Project Title
          </span>{" "}
          <span style={{ color: theme.textMain }}>{currentRoom.projectName}</span>
          <span style={{ color: theme.textSub }}>
            Description Metadata
          </span>{" "}
          <span style={{ color: theme.textMain }}>
            {currentRoom.description || "—"}
          </span>
        </div>
      </section>

      {/* INTERNAL TEAM DIRECTORY DROPDOWN MODAL */}
      {isMemberModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1100,
          }}
        >
          <div
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              padding: "24px",
              width: "400px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h3
              style={{
                margin: "0 0 4px 0",
                color: theme.textMain,
                fontSize: "16px",
              }}
            >
              Add Team Member
            </h3>
            <p
              style={{
                fontSize: "12px",
                color: theme.textSub,
                marginBottom: "16px",
              }}
            >
              Select an employee from your organization directory to grant entry
              access.
            </p>

            <input
              type="text"
              placeholder="Search name..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                background: theme.bg,
                color: theme.textMain,
                fontSize: "14px",
                marginBottom: "16px",
              }}
            />

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {filteredAvailableEmployees.length === 0 ? (
                <p
                  style={{
                    fontSize: "13px",
                    color: theme.textSub,
                    textAlign: "center",
                    padding: "20px 0",
                    fontStyle: "italic",
                  }}
                >
                  No eligible employees found.
                </p>
              ) : (
                filteredAvailableEmployees.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAddMember(u)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                      padding: "10px",
                      background: "transparent",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = darkMode
                        ? "#2d3748"
                        : "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <Avatar
                      src={u.profilePictureUrl}
                      sx={{ width: 32, height: 32, borderRadius: "8px" }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.textMain,
                        }}
                      >
                        {u.first_name} {u.last_name}
                      </div>
                      <div style={{ fontSize: "11px", color: theme.textSub }}>
                        {u.email}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                borderTop: `1px solid ${theme.border}`,
                paddingTop: "12px",
              }}
            >
              <button
                onClick={() => {
                  setIsMemberModalOpen(false);
                  setMemberSearch("");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: theme.textSub,
                  padding: "8px 16px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
